import type { ChatLine } from "~/protocol";

// Display names for what the agents do (spec: web-workspace-spec "Run view").
// Presentational and web-only: the transcript keeps the tool names, the feed
// shows these. One table for the label and the one parameter worth showing;
// an unknown tool falls back to its humanized name.

interface Label {
  label: string;
  /** The parameter shown after the label, if any. */
  param?: string;
}

const TOOL_LABEL: Record<string, Label> = {
  browser_navigate: { label: "Opened", param: "url" },
  browser_navigate_back: { label: "Went back" },
  browser_snapshot: { label: "Read the page" },
  browser_click: { label: "Clicked", param: "element" },
  browser_type: { label: "Typed into", param: "element" },
  browser_fill_form: { label: "Filled a form" },
  browser_select_option: { label: "Selected", param: "element" },
  browser_press_key: { label: "Pressed", param: "key" },
  browser_hover: { label: "Hovered", param: "element" },
  browser_scrollto: { label: "Scrolled to", param: "element" },
  browser_wait_for: { label: "Waited for the page" },
  browser_evaluate: { label: "Ran a script on the page" },
  browser_close: { label: "Closed the browser" },
  capture_page: { label: "Captured the page as", param: "name" },
  capture_viewport: { label: "Captured the screen as", param: "name" },
  capture_element_bounds: { label: "Measured elements" },
  capture_timestamp: { label: "Marked", param: "name" },
  capture_comment: { label: "Noted", param: "text" },
  request_user_action: { label: "Asked you" },
  request_credentials: { label: "Asked for a sign-in to", param: "domain" },
  spawn_explorer_agent: { label: "Sent the explorer out" },
  edl_validate: { label: "Checked the cut", param: "edl" },
  edl_preview: { label: "Previewed", param: "edl" },
  edl_render: { label: "Rendered", param: "edl" },
  create_voiceover: { label: "Recorded the voiceover" },
  create_background_music: { label: "Composed the music" },
  list_files: { label: "Listed the files" },
  read_file: { label: "Read", param: "filename" },
  view_media: { label: "Viewed", param: "filename" },
  open_tab: { label: "Showed you", param: "filename" },
  // The user's unlock, written into the transcript by the workspace (not a model tool).
  unlock_video: { label: "Removing the watermark from", param: "fileName" },
  load_skill: { label: "Loaded", param: "name" },
  web_search: { label: "Searched the web" },
  webfetch: { label: "Downloaded", param: "urls" },
  convert_media: { label: "Converted", param: "source" },
};

const DETAIL_MAX = 60;

/** The file a URL names: its last path segment, else its host. */
function fileOf(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split("/").pop();
    return last ? decodeURIComponent(last) : u.hostname;
  } catch {
    return url;
  }
}

/** A list of URLs as the files they name: the first two, the rest counted. */
function fileList(urls: string[]): string {
  const names = urls.map(fileOf);
  const rest = names.length - 2;
  const shown = names.slice(0, 2).join(", ");
  return rest > 0 ? `${shown}, and ${rest} other file${rest === 1 ? "" : "s"}` : shown;
}

/** A URL as hostname + path, a list of URLs as their files, anything else trimmed to one
 * short line. */
function detail(value: unknown): string {
  if (Array.isArray(value))
    return fileList(value.filter((v): v is string => typeof v === "string"));
  if (typeof value !== "string") return "";
  try {
    const u = new URL(value);
    return (u.hostname + u.pathname).replace(/\/$/, "");
  } catch {
    const flat = value.replace(/\s+/g, " ").trim();
    return flat.length > DETAIL_MAX ? `${flat.slice(0, DETAIL_MAX)}…` : flat;
  }
}

/** The step text of a tool call: label + the one parameter, e.g. "Opened lotsofcsvs.com/pricing". */
export function toolStep(name: string, params: unknown): { label: string; detail: string } {
  const known = TOOL_LABEL[name];
  if (!known)
    return { label: name.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase()), detail: "" };
  const p = (params ?? {}) as Record<string, unknown>;
  return { label: known.label, detail: known.param ? detail(p[known.param]) : "" };
}

/** A line as one step inside a card. */
export function stepText(line: ChatLine): { label: string; detail: string } {
  const b = line.body;
  if (b.kind === "tool_call") return toolStep(b.name, b.params);
  if (b.kind === "patch") return { label: b.ok ? "Edited" : "Failed to edit", detail: b.op.path };
  return line.agent === "user" ? { label: "You:", detail: b.text } : { label: b.text, detail: "" };
}

// A card's title comes from the heaviest thing it did, in this order.
const CARD_TITLES: { names: string[]; patch?: true; busy: string; done: string }[] = [
  { names: ["unlock_video"], busy: "Removing the watermark", done: "Removed the watermark" },
  { names: ["edl_render"], busy: "Filming the video", done: "Filmed the video" },
  {
    names: ["create_voiceover", "create_background_music"],
    busy: "Recording the audio",
    done: "Recorded the audio",
  },
  { names: ["edl_validate", "edl_preview"], busy: "Cutting the video", done: "Cut the video" },
  { names: [], patch: true, busy: "Writing the script", done: "Wrote the script" },
];
// The first rows render video (an unlock is a render too): the pill reads "filming".
const FILMING_ROWS = 2;

/** Index into CARD_TITLES of the heaviest tool in the lines, or -1. */
export function cardRank(lines: ChatLine[]): number {
  return CARD_TITLES.findIndex((t) =>
    lines.some(({ body }) =>
      body.kind === "tool_call" ? t.names.includes(body.name) : body.kind === "patch" && t.patch,
    ),
  );
}

export const isFilming = (lines: ChatLine[]): boolean => {
  const rank = cardRank(lines);
  return rank >= 0 && rank < FILMING_ROWS;
};

/** Host of the first page the explorer opened in these lines, else `fallback`. */
export function exploredHost(lines: ChatLine[], fallback: string): string {
  for (const { body } of lines) {
    if (body.kind === "tool_call" && body.name === "browser_navigate") {
      const url = (body.params as { url?: unknown } | null)?.url;
      if (typeof url === "string") return hostOf(url);
    }
  }
  return fallback;
}

export function cardTitle(
  lines: ChatLine[],
  explorer: boolean,
  busy: boolean,
  host: string,
): string {
  if (explorer) return `${busy ? "Exploring" : "Explored"} ${exploredHost(lines, host)}`;
  const rank = cardRank(lines);
  const t = CARD_TITLES[rank];
  if (t) return busy ? t.busy : t.done;
  return busy ? "Working" : "Done";
}

/** "www."-less hostname of a URL; the text itself when it is not one. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}
